import { useQuery } from '@tanstack/react-query';
import {
  type AppEnvironment,
  BACKUP_TRIGGER_LABELS,
  type BackupRunResponse,
  type BackupsResponse,
  ENVIRONMENT_LABELS,
} from '@k-management/shared';
import { useState } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { BackupHealthNotice } from '../../components/BackupHealthNotice.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { apiRequest } from '../../lib/api.js';
import { formatDuration, formatFileSize, formatTimestamp } from '../../lib/format.js';
import { queryKeys } from '../../lib/query-keys.js';

/** The GitHub workflow that restores a backup (docs/adr/ADR-016). */
const RESTORE_WORKFLOW_URL = 'https://github.com/K-Rev/K-Management/actions/workflows/restore.yml';

/** The branch the workflow runs on decides the environment: its credentials work only there. */
const RESTORE_BRANCH: Partial<Record<AppEnvironment, string>> = {
  production: 'main',
  test: 'develop',
};

/**
 * Administrators: outcome of the database backups of this environment
 * (docs/adr/ADR-006), their check and the restores (ADR-016). A restore starts
 * from GitHub, never from here: a stolen administrator account cannot wipe the
 * data.
 */
export function BackupsPage() {
  const { environment } = useAuth();
  const [restoring, setRestoring] = useState<BackupRunResponse | null>(null);
  const backups = useQuery({
    queryKey: queryKeys.backups,
    queryFn: () => apiRequest<BackupsResponse>('/backups'),
  });

  return (
    <section className="stack">
      <div className="page-header">
        <h1>Backup</h1>
      </div>
      {backups.isPending && <p className="muted">Caricamento…</p>}
      {backups.isError && <p className="error">{backups.error.message}</p>}
      {backups.data && (
        <>
          <BackupHealthNotice health={backups.data.health} />
          <p className="page-hint">
            Il database di questo ambiente
            {environment ? ` (${ENVIRONMENT_LABELS[environment].toLowerCase()})` : ''} viene salvato
            ogni notte e prima di ogni rilascio in produzione. Ogni file viene subito riletto per
            controllare che si possa ripristinare. I file restano {backups.data.retentionDays}{' '}
            giorni nel bucket privato, poi vengono eliminati in automatico.
          </p>
          {backups.data.runs.length > 0 && (
            <div className="table-wrap card flush">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Esito</th>
                    <th>Verifica</th>
                    <th className="numeric">Dimensione</th>
                    <th className="numeric">Durata</th>
                    <th>Versione</th>
                    <th>File o errore</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {backups.data.runs.map((run) => (
                    <tr key={run.id}>
                      <td>{formatTimestamp(run.finishedAt)}</td>
                      <td>{BACKUP_TRIGGER_LABELS[run.trigger]}</td>
                      <td>
                        {run.status === 'succeeded' ? (
                          <span className="ok">Riuscito</span>
                        ) : (
                          <span className="error">Non riuscito</span>
                        )}
                      </td>
                      <td>
                        <Verification run={run} />
                      </td>
                      <td className="numeric">
                        {run.sizeBytes === null ? '—' : formatFileSize(run.sizeBytes)}
                      </td>
                      <td className="numeric">{formatDuration(run.startedAt, run.finishedAt)}</td>
                      <td>
                        {run.release} <small className="muted">{run.buildVersion}</small>
                      </td>
                      <td className="details">
                        {run.status === 'succeeded' ? (
                          <span title={run.sha256 ? `SHA-256 ${run.sha256}` : undefined}>
                            {run.objectName}
                          </span>
                        ) : (
                          <span className="error">{run.error}</span>
                        )}
                      </td>
                      <td>
                        {run.status === 'succeeded' && run.objectName && !run.verifyError && (
                          <button type="button" className="link" onClick={() => setRestoring(run)}>
                            Come ripristinare…
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>Ripristini</h2>
          {backups.data.restores.length === 0 ? (
            <p className="muted">Nessun ripristino in questo ambiente.</p>
          ) : (
            <div className="table-wrap card flush">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Esito</th>
                    <th>Backup ripristinato</th>
                    <th>Backup di sicurezza</th>
                    <th>Avviato da</th>
                    <th className="numeric">Durata</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.data.restores.map((restore) => (
                    <tr key={restore.id}>
                      <td>{formatTimestamp(restore.finishedAt)}</td>
                      <td>
                        {restore.status === 'succeeded' ? (
                          <span className="ok">Riuscito</span>
                        ) : (
                          <span className="error">{restore.error ?? 'Non riuscito'}</span>
                        )}
                      </td>
                      <td className="details">{restore.objectName}</td>
                      <td className="details">{restore.safetyObjectName ?? '—'}</td>
                      <td>{restore.requestedBy ?? '—'}</td>
                      <td className="numeric">
                        {formatDuration(restore.startedAt, restore.finishedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {restoring && environment && (
        <RestoreDialog
          run={restoring}
          environment={environment}
          onClose={() => setRestoring(null)}
        />
      )}
    </section>
  );
}

function Verification({ run }: { run: BackupRunResponse }) {
  if (run.status !== 'succeeded') return <>—</>;
  if (run.verifyError) return <span className="error">Non leggibile: {run.verifyError}</span>;
  if (run.verifiedTables !== null) {
    return <span className="ok">Leggibile, {run.verifiedTables} tabelle</span>;
  }
  return <span className="muted">Non verificato</span>;
}

/** The steps to restore a backup from GitHub (docs/operazioni/ripristino.md). */
function RestoreDialog({
  run,
  environment,
  onClose,
}: {
  run: BackupRunResponse;
  environment: AppEnvironment;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const branch = RESTORE_BRANCH[environment];
  const label = ENVIRONMENT_LABELS[environment].toLowerCase();
  const copy = () => {
    void navigator.clipboard.writeText(run.objectName ?? '').then(() => setCopied(true));
  };
  return (
    <ConfirmDialog
      title={`Ripristinare il backup del ${formatTimestamp(run.finishedAt)}`}
      confirmLabel={branch ? 'Apri il comando su GitHub' : 'Chiudi'}
      onConfirm={() => {
        if (branch) window.open(RESTORE_WORKFLOW_URL, '_blank', 'noopener');
        onClose();
      }}
      onCancel={onClose}
    >
      {!branch ? (
        <p>In sviluppo il database si ricrea da sé a ogni avvio: non c’è nulla da ripristinare.</p>
      ) : (
        <>
          <p className={environment === 'production' ? 'notice error-notice' : 'notice info'}>
            Tutto quello che è stato inserito o modificato in {label} dopo il{' '}
            {formatTimestamp(run.finishedAt)} andrà perso. Prima del ripristino viene fatto un
            backup di sicurezza, con cui si può tornare indietro.
            {environment === 'production' && ' Avvisa gli utenti prima di iniziare.'}
          </p>
          <ol className="restore-steps">
            <li>
              Copia il nome del file: <code>{run.objectName}</code>{' '}
              <button type="button" className="link" onClick={copy}>
                {copied ? 'Copiato' : 'Copia'}
              </button>
            </li>
            <li>
              Su GitHub apri «Ripristino del database» e premi «Run workflow». In «Use workflow
              from» scegli il branch <code>{branch}</code>: è quello di {label}.
            </li>
            <li>
              Incolla il nome del file e scrivi la conferma <code>RIPRISTINA {label}</code>.
            </li>
            <li>Segui l’esecuzione: alla fine il ripristino compare qui sotto, in «Ripristini».</li>
          </ol>
        </>
      )}
    </ConfirmDialog>
  );
}
